/**
 * Reading an access token's claims on the client.
 *
 * Decoded, never verified -- the client holds no key. What is read here only decides what the screen offers;
 * every endpoint enforces the same claims on the server.
 */

/**
 * The bytes `atob` produced, read as UTF-8.
 *
 * `atob` yields one character per byte, so a non-ASCII claim — a name with an
 * accent in it — would otherwise arrive mojibake. Re-decoding through URI
 * escapes puts the UTF-8 back together; anything that is not valid UTF-8 falls
 * back to the raw bytes rather than discarding the whole payload, because the
 * claims read from it are ASCII either way.
 */
function utf8(binary: string): string {
  try {
    return decodeURIComponent(
      Array.from(binary, (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
    );
  } catch {
    return binary;
  }
}

/** Decodes one base64url segment. Returns null rather than throwing on anything malformed. */
function decodeSegment(segment: string): unknown {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    const binary = window.atob(padded);
    return JSON.parse(utf8(binary)) as unknown;
  } catch {
    return null;
  }
}

/** An access token's payload, decoded but not verified; null for a missing or unreadable token. */
export function decodeTokenPayload(token: string | null | undefined): unknown {
  if (!token) return null;
  const segment = token.split('.')[1];
  if (segment === undefined || segment === '') return null;
  return decodeSegment(segment);
}
